// Frontend environment configuration
interface EnvironmentConfig {
  apiUrl: string;
  environment: 'development' | 'staging' | 'production';
  enableLogging: boolean;
  logLevel: 'debug' | 'info' | 'warn' | 'error';
  uploadMaxSize: number;
  supportedImageTypes: string[];
  paddleClientToken: string;
  paddleEnvironment: 'sandbox' | 'production';
}

export const getApiUrl = (): string => {
  // Only use env var if it is a non-empty string.
  // Empty string in dev docker-compose means "unset" — fall through to dynamic logic.
  if (process.env.REACT_APP_API_URL) {
    // CF Pages subdomain constraint: dots aren't allowed (api.nice → api-nice).
    // Fix the old incorrect domain if it slipped into the build env.
    return process.env.REACT_APP_API_URL.replace(
      'api.nice.jetsmedia.top',
      'api-nice.jetsmedia.top'
    );
  }

  if (typeof window === 'undefined') {
    return '';
  }

  const currentHost = window.location.hostname;

  // Cloudflare Pages and the production frontend use a dedicated API subdomain.
  // This avoids depending on a /api proxy on the static frontend origin.
  const isCloudflareOrProdFrontend =
    currentHost === 'nice.jetsmedia.top' ||
    currentHost === 'www.nice.jetsmedia.top' ||
    currentHost.endsWith('.pages.dev');

  if (process.env.NODE_ENV === 'production' && isCloudflareOrProdFrontend) {
    return 'https://api-nice.jetsmedia.top';
  }

  // Dev: if accessed via a raw IP address or localhost, the browser talks
  // directly to the backend (no nginx in front), so we need the explicit port.
  // If accessed via a proper domain (e.g. dev-nice.jetsmedia.top), nginx
  // is in front and proxies /api/ to the backend — use relative paths.
  const isDirectAccess = /^(\d{1,3}\.){3}\d{1,3}$/.test(currentHost) || currentHost === 'localhost';
  // Port 2060: the backend NODE_ENV=development server listens on PORT=2060 (set in docker-compose.dev.yml).
  // Port 2051 is the code default but overridden by the env var in the dev container.
  return isDirectAccess ? `http://${currentHost}:2060` : '';
};

export const resolveApiUrl = (path: string): string => {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  const apiUrl = getApiUrl();
  if (!apiUrl) {
    return path;
  }

  return `${apiUrl}${path.startsWith('/') ? path : `/${path}`}`;
};

const config: EnvironmentConfig = {
  apiUrl: getApiUrl(),
  environment: (process.env.NODE_ENV as 'development' | 'staging' | 'production') || 'development',
  enableLogging: process.env.REACT_APP_ENABLE_LOGGING === 'true',
  logLevel: (process.env.REACT_APP_LOG_LEVEL as 'debug' | 'info' | 'warn' | 'error') || 'error',
  uploadMaxSize: parseInt(process.env.REACT_APP_UPLOAD_MAX_SIZE || '10485760'), // 10MB
  supportedImageTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  paddleClientToken: process.env.REACT_APP_PADDLE_CLIENT_TOKEN || '',
  paddleEnvironment: (process.env.REACT_APP_PADDLE_ENVIRONMENT as 'sandbox' | 'production') || 'sandbox',
};

// Validation - Allow empty string for production nginx proxy
if (config.apiUrl === undefined || config.apiUrl === null) {
  throw new Error('API URL is required');
}

export default config;
